import type { LinkProps } from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  type Country,
  CountryCodeEnum,
  LanguageSlugEnum,
  type Locale,
} from '@/types/regionTypes';
import { isPathMatch } from '@/utils/urlUtils';

import type { BreadcrumbItem } from '@/components/Breadcrumb';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import { SectionHeader } from '@/components/SectionHeader';
import {
  MY_DETAILS_MENU,
  ORDERS_MENU,
} from '@/components/SideNavigationMenu/consts';
import { Typography } from '@/components/Typography';
import { useCustomSignOut } from '@/hooks/useCustomSignOut';
import { useIsMdUp } from '@/hooks/useMedia';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { paths } from '@/lib/paths';
import { StorageHelper } from '@/lib/storage';
import { generateDataTestId } from '@/lib/tests';
import { commonMessages } from '@/messages/common';
import * as M from '@/messages/myAccount';
import { serviceMessages } from '@/messages/service';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { UserState } from '@/states/user';
import { wishlistActions } from '@/states/wishlistState';
import { getFullName } from '@/views/MyAccount/utils';

import * as S from './AccountHeader.styles';

export type BreadcrumbItems = {
  label: {
    defaultMessage: string;
    id: string;
  };
  target: LinkProps['href'];
};

export interface AccountHeaderProps {
  breadcrumbItems?: BreadcrumbItems[];
  hasBreadcrumbs: boolean;
  logoutLink?: string;
  showClubMessage?: boolean;
}

export interface GetDisplayNameProps {
  country: Country;
  locale: Locale;
  user: UserState['user'];
}

/**
 * Get user display name according to market and language.
 * Display the name as "Kanji Last name (highlight) + Kanji First name + 様", only when
 * - Japan market
 * - AND Japanese or Chinese language
 * - AND user is with Kanji last name and Kanji first name
 * Otherwise, display the name as "Romaji First + Romaji Lat"
 * @param country current selected market
 * @param locale current selected language
 * @param user logged-in user
 */
export const getDisplayName = ({
  country,
  locale,
  user,
}: GetDisplayNameProps): string => {
  if (
    country.code === CountryCodeEnum.JP &&
    (locale.slug === LanguageSlugEnum.ja || locale.slug === LanguageSlugEnum.zh)
  ) {
    const kanjiFullName = getFullName(
      country,
      locale,
      getMetaValue(user?.metadata ?? [], ACCOUNT_METADATA.first_name_kanji),
      getMetaValue(user?.metadata ?? [], ACCOUNT_METADATA.last_name_kanji),
    );

    if (kanjiFullName) {
      return `${kanjiFullName} 様`;
    }
  }

  return getFullName(country, locale, user?.firstName, user?.lastName);
};

// Tab paths come straight from the side-nav config, so adding/removing a nav
// item keeps this in sync automatically (myPreferences, myAccount, addresses,
// and the order sub-pages).
const SIDE_NAV_PATHS = [...MY_DETAILS_MENU, ...ORDERS_MENU].map(
  (item) => item.path,
);

// Section landings are not part of the side-nav, so list them explicitly.
const MY_ACCOUNT_SECTION_LANDINGS = [
  paths.myAccount.myDetails.asPath(),
  paths.myAccount.myWatches.asPath(),
  paths.myAccount.myWishlist.asPath(),
  paths.myAccount.myOrders.asPath(),
];

const MY_ACCOUNT_ROOT_PATHS = [
  ...SIDE_NAV_PATHS,
  ...MY_ACCOUNT_SECTION_LANDINGS,
];

export const AccountHeader: React.FC<AccountHeaderProps> = ({
  breadcrumbItems = [
    {
      label: commonMessages.home,
      target: paths.url(),
    },
    {
      label: M.myAccountMessages.myAccount,
      target: paths.myAccount.url(),
    },
  ],
  hasBreadcrumbs = false,
  logoutLink = paths.home.url(),
  showClubMessage = false,
}): JSX.Element | null => {
  const [urlPathNameSplit, setUrlPathNameSplit] = useState<string | null>(null);
  const [isClient, setIsClient] = useState(false);
  const isProcessingRef = useRef(false);

  const { back, pathname: routerPathname, push } = useRouter();
  // Using a regular expression to exclude the fixed prefix '/[lang]'
  const pathname = routerPathname.replace(/^\/[^/]+/, '');
  const isMdUp = useIsMdUp();
  const { formatMessage } = useIntl();

  const { signOut } = useCustomSignOut();
  const {
    snapshot: { isAuthenticated, user },
  } = useUser();
  const storageHelper = new StorageHelper();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const formatBreadcrumbItems: BreadcrumbItem[] = breadcrumbItems.map(
    (item) => ({
      ...item,
      label: formatMessage(item.label),
    }),
  );

  useEffect(() => {
    if (!pathname) return;

    let lastURLSegment = null;

    switch (true) {
      case pathname.startsWith(paths.wishlist.asPath()):
        lastURLSegment = formatMessage(M.myAccountMessages.wishlist);
        break;
      case pathname.startsWith(paths.myAccount.myOrders.asPath()):
        lastURLSegment = formatMessage(M.myAccountMessages.accountOrdersTitle);
        break;
      case pathname.startsWith(paths.myAccount.myDetails.asPath()):
      case pathname.startsWith(paths.myAccount.myAddresses.history.asPath()):
        lastURLSegment = formatMessage(commonMessages.accountDetailsTitle);
        break;
      case pathname.startsWith(paths.myAccount.myWishlist.asPath()):
        lastURLSegment = formatMessage(
          M.myAccountMessages.accountWishlistTitle,
        );
        break;
      case pathname.startsWith(paths.myAccount.myWatches.asPath()):
        lastURLSegment = formatMessage(M.myAccountMessages.accountWatchesTitle);
        break;
      case pathname.startsWith(paths.service.request.asPath()):
      case pathname.startsWith(paths.service.result.asPath({ id: '' })):
        lastURLSegment = formatMessage(serviceMessages.requestAService);
        break;
      case pathname.startsWith(paths.service.details.asPath({ params: '' })):
        lastURLSegment = formatMessage(serviceMessages.myServiceDetails);
        break;
      case pathname.startsWith(
        paths.service.quote.asPath({ repair: '', quote: '' }),
      ):
        lastURLSegment = formatMessage(serviceMessages.myServiceQuote);
        break;
      case pathname.startsWith(paths.service.payment.confirmation.asPath()):
        lastURLSegment = formatMessage(serviceMessages.requestAService);
        break;
      case isPathMatch(pathname, paths.myAccount.asPath()):
        lastURLSegment = formatMessage(serviceMessages.myAccount);
        break;
      default:
        lastURLSegment = pathname.split('/').pop()?.replace(/-/g, ' ');
        break;
    }

    setUrlPathNameSplit(lastURLSegment || null);
  }, [pathname, isClient]);

  const handleSignOutClick = async () => {
    if (isProcessingRef.current || !isAuthenticated) return;
    isProcessingRef.current = true;
    await push(logoutLink);
    await storageHelper.removeLocalStorage('wishList');
    wishlistActions.clear();
    await signOut();
    isProcessingRef.current = false;
  };

  const signOutLink = () => {
    if (!isAuthenticated) return null;
    return (
      <S.LogoutButton
        onClick={handleSignOutClick}
        {...generateDataTestId('button', 'signOut')}
      >
        <Typography>{formatMessage(commonMessages.logOut)}</Typography>
      </S.LogoutButton>
    );
  };

  const handleBackClick = () => {
    const isRootSectionPage = MY_ACCOUNT_ROOT_PATHS.some((rootPath) =>
      isPathMatch(pathname, rootPath),
    );

    // Desktop only: from a root section/tab page, go to the My Account overview
    // instead of browser history. Mobile and nested routes keep `back()`.
    if (isMdUp && isRootSectionPage) {
      push(paths.myAccount.url());
      return;
    }

    back();
  };

  const breadcrumbOrBackBtn = (hasBreadcrumbs: boolean) => {
    return hasBreadcrumbs ? (
      <Breadcrumb
        data-test-id='AccountHeaderBreadcrumb'
        items={formatBreadcrumbItems}
      />
    ) : (
      <LegacyButton
        data-test-id='AccountHeaderBackBtn'
        isUpperCase={false}
        label={formatMessage(commonMessages.back)}
        leftContent={<Icon name='chevronLeft' />}
        onClick={handleBackClick}
        variant='link'
        width='auto'
      />
    );
  };

  const clubMessage = (showClubMessage: boolean) => {
    if (!showClubMessage || !isAuthenticated) {
      return null;
    }
    return (
      <Typography data-test-id='AccountHeaderClubMessage'>
        Join{' '}
        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
          Breitling Club
        </span>{' '}
        to enjoy more benefits
      </Typography>
    );
  };

  const { country, locale } = useRegionProvider();
  const nameDisplay = getDisplayName({ country, locale, user });

  if (!isClient) {
    return null;
  }

  if (!isMdUp) {
    return (
      <S.HeaderWrapper data-test-id='AccountHeaderWrapper'>
        <Breadcrumb.Wrapper>
          <Flex justifyContent='space-between' mb='s200'>
            {breadcrumbOrBackBtn(hasBreadcrumbs)}
            {signOutLink()}
          </Flex>
          <Flex justifyContent='center'>
            {urlPathNameSplit && (
              <SectionHeader
                disableGutters
                htmlElementTag='h1'
                sizeMobile={50}
                text={urlPathNameSplit}
                textAlign='center'
                {...generateDataTestId('text', 'currentPath')}
              />
            )}
          </Flex>
        </Breadcrumb.Wrapper>
      </S.HeaderWrapper>
    );
  }
  if (isMdUp) {
    return (
      <S.HeaderWrapper data-test-id='AccountHeaderWrapper'>
        <Breadcrumb.Wrapper>
          {breadcrumbOrBackBtn(hasBreadcrumbs)}
          <Flex justifyContent='space-between' width='100%'>
            <Flex flex='1'></Flex>
            <Flex alignItems='center' flex='1' justifyContent='center'>
              {urlPathNameSplit && (
                <SectionHeader
                  disableGutters
                  htmlElementTag='h1'
                  sizeDesktop={50}
                  text={urlPathNameSplit}
                  textAlign='center'
                  {...generateDataTestId('text', 'currentPath')}
                />
              )}
            </Flex>
            <Flex
              alignItems='end'
              flex='1'
              flexDirection='column'
              gridGap={1}
              justifyContent='center'
            >
              {user && (
                <SectionHeader
                  className={CONTENTSQUARE_MASKED}
                  color='black'
                  disableGutters
                  numberOfColoredWords={1}
                  sizeDesktop={24}
                  sizeMobile={24}
                  text={nameDisplay}
                  textAlign='center'
                  {...generateDataTestId('text', 'userName')}
                />
              )}
              {clubMessage(showClubMessage)}
              {signOutLink()}
            </Flex>
          </Flex>
        </Breadcrumb.Wrapper>
      </S.HeaderWrapper>
    );
  }
  return null;
};

AccountHeader.displayName = 'AccountHeader';
